import type { ComputedRef, Ref } from 'vue';
import { isReactive, isRef, reactive, ref, unref, watch } from 'vue';
import type { LocationQuery } from 'vue-router';
import { useRoute } from 'vue-router';
import { extend } from 'dayjs';
import { isEqual } from 'lodash-es';
import type { PagedParameters, PagedResult } from '@shared/models/paged.collection';
import type { ServiceError, ServiceResult } from '@core/models/actionResponse';
import { isServiceError, isServiceSuccess } from '@core/models/actionResponse';
import type { ToastifyOptions } from '@core/utils/useToaster';
import { useToaster } from '@core/utils/useToaster';

export interface AsyncRequestOptions {
  initialLoading: boolean;
  errorMsg?: string;
  successMsg?: string;
}

// export function useAsyncRequest<T>(fn: () => Promise<T>, options?: AsyncRequestOptions) {
//   const hasOptions = typeof options !== 'undefined';
//   const loading = ref(hasOptions ? options.initialLoading : false);

//   const execute = async () => {
//     loading.value = true;
//     let data: T | undefined = undefined;
//     try {
//       data = await fn();
//     } catch (e) {
//       console.error(e);
//     }
//     loading.value = false;
//     return data;
//   };

//   return { loading, execute };
// }

// export function useAsyncRequest<T, A extends unknown[]>(fn: (...args: A) => Promise<T>, options?: AsyncRequestOptions) {
//   const hasOptions = typeof options !== 'undefined';
//   const loading = ref(hasOptions ? options.initialLoading : false);

//   const execute = async (...args: A): Promise<T | undefined> => {
//     loading.value = true;
//     let data: T | undefined = undefined;
//     try {
//       data = await fn(...args);
//     } catch (e) {
//       console.error(e);
//     }
//     loading.value = false;
//     return data;
//   };

//   return { loading, execute };
// }

export function useAsyncRequest<T, A extends unknown[]>(fn: (...args: A) => Promise<T>, options?: AsyncRequestOptions) {
  const hasOptions = typeof options !== 'undefined';
  const loading = ref(hasOptions ? options.initialLoading : false);

  const execute = async (...args: A): Promise<T | undefined> => {
    loading.value = true;
    let data: T | undefined = undefined;
    try {
      data = await fn(...args);
    } catch (e) {
      console.error(e);
    }
    loading.value = false;
    return data;
  };

  return { loading, execute };
}

export function useAsyncResult<T>(
  fn: () => Promise<T>,
  options?: AsyncRequestOptions,
): { loading: Ref<boolean>; execute: () => Promise<T | undefined>; result: Ref<T | undefined> } {
  const result = ref<T>();
  const { loading, execute } = useAsyncRequest(async () => {
    const data = (await fn()) as T;
    result.value = data;
    return data;
  }, options);

  return { loading, execute, result };
}

export function useAsyncModel<T extends object | []>(fn: () => Promise<T>) {
  const { result: model, execute: load } = useAsyncResult(fn);
  return { model, load };
}

export function useAsyncPagedResult<T, TParameters extends object = PagedParameters>(
  fn: (params: TParameters) => Promise<T>,
  pagedParams: Ref<TParameters> | ComputedRef<TParameters>,
  options?: AsyncRequestOptions,
  watchOptions: 'pagedParams' | 'route' = 'route',
) {
  const route = useRoute();
  const { loading, execute, result } = useAsyncResult(async () => fn(unref(pagedParams)), options);

  const onWatchChanged = (newValue: TParameters | LocationQuery, oldValue: TParameters | LocationQuery) => {
    const newPageParams = newValue as TParameters;
    const oldPageParams = oldValue as TParameters;
    if (!isEqual(newPageParams, oldPageParams)) {
      execute();
    }
  };

  if (watchOptions === 'route') {
    watch(() => route.query, onWatchChanged);
  } else watch(pagedParams, onWatchChanged);

  return {
    loading,
    execute,
    result,
  };
}

export function useAsyncServiceResult<T>(fn: () => Promise<ServiceResult<T>>, options?: AsyncRequestOptions) {
  const error = ref<ServiceError>();
  const { result, execute, loading } = useAsyncResult(async () => {
    const result = await fn();
    if (isServiceError(result)) {
      error.value = result;
      return undefined;
    }
    return result.data;
  }, options);
  return { result, loading, execute, error };
}

export function useAsyncServiceRequest(
  fn: () => Promise<ServiceResult<any>>,
  onSuccess?: () => any,
  options?: AsyncRequestOptions,
) {
  const error = ref<ServiceError>();
  const { execute, loading } = useAsyncRequest(async () => {
    const result = await fn();
    if (isServiceError(result)) {
      error.value = result;
    } else if (onSuccess) {
      onSuccess();
    }
  }, options);
  return { execute, loading, error };
}

export function useToastyAsyncServiceResult<T>(
  fn: () => Promise<ServiceResult<T>>,
  options?: AsyncRequestOptions,
  errorToastOptions?: ToastifyOptions,
) {
  const { toastify } = useToaster();
  const { result, execute, loading } = useAsyncResult(async () => {
    const result = await fn();
    if (isServiceError(result)) {
      toastify(result, null, options?.errorMsg, errorToastOptions);
      return null;
    }
    if (isServiceSuccess(result) && options?.successMsg) toastify(result, options?.successMsg);
    return result.data;
  }, options);
  return { result, loading, execute };
}

export function useConcatenatedPagedResults<T>(
  fn: (params: PagedParameters) => Promise<PagedResult<T>>,
  defaults?: Partial<PagedParameters>,
) {
  const params = reactive<PagedParameters>({ currentPage: 1, pageSize: 50, ...defaults });
  const results = ref<T[]>([]) as Ref<T[]>;
  const loading = ref(false);
  const complete = ref(false);

  const execute = async () => {
    if (complete.value) return;
    loading.value = true;
    const nextResults = await fn(params);
    loading.value = false;
    results.value = [...results.value, ...nextResults.resultItems];
    complete.value = nextResults.currentPage * nextResults.pageSize > nextResults.totalRecords;
  };
  const reset = () => {
    results.value = [];
    complete.value = false;
    params.currentPage = 1;
  };
  watch(() => ({ currentPage: params.currentPage, filter: params.filter }), execute);
  return {
    reset,
    params,
    execute,
    loading,
    results,
    /** True once the last page has been appended — nothing more to fetch until `reset`. */
    complete,
  };
}
